import Decimal from "decimal.js";
import { GetCartQuery } from "../types/storefront.generated";
import { ClubLabel } from "../types/types";
import config from "./config";
import { currencyToFloat } from "./currencyToFloat";
import { isPartnerClub, mapClubLabel } from "./mapClubLabel";

type ClubPricedLine = {
  merchandise: {
    price: {
      amount: string;
    };
    product: Record<string, any>;
    [key: string]: any;
  };
};

export type ClubPricingContext = {
  activeClub: ClubLabel;
  customerId: string | null;
  hasDiscountCode: boolean;
  isYotpoDiscount: boolean;
  isClubPricingDiscount: boolean;
};

export const CLUB_PRICING_DISCOUNT_CODE = "BUSHBUCKCLUB";

export function isClubPricingDiscountCode(code: string): boolean {
  return code.toUpperCase() === CLUB_PRICING_DISCOUNT_CODE;
}

export function getCartClubPricingContext(cart: NonNullable<GetCartQuery["cart"]>): ClubPricingContext {
  let activeClub = mapClubLabel(cart.activeClub?.value ?? config.activeClub);
  let customerId = cart.buyerIdentity.customer?.id ?? null;
  const isClubPricingDiscount = cart.discountCodes.some(
    code => code.applicable && isClubPricingDiscountCode(code.code),
  );

  if (isClubPricingDiscount) {
    customerId = customerId ?? "club-pricing-discount";
  }

  for (const code of cart.discountCodes) {
    if (config.partnerCodes.includes(code.code.toUpperCase())) {
      activeClub = "clubPT";
      customerId = customerId ?? "partnercode";
      break;
    }
  }

  const hasDiscountCode = cart.discountCodes.some(code => code.applicable);
  const discountCode = hasDiscountCode ? cart.discountCodes[0].code : null;

  return {
    activeClub,
    customerId,
    hasDiscountCode,
    isYotpoDiscount: Boolean(discountCode && isYotpoDiscountCode(discountCode)),
    isClubPricingDiscount,
  };
}

export function getLineClubPrice(
  line: ClubPricedLine,
  activeClub: ClubLabel,
  customerId: string | null,
  hasDiscountCode: boolean,
  isYotpoDiscount = false,
  isClubPricingDiscount = false,
) {
  if ((!customerId && !isPartnerClub(activeClub)) ||
    (hasDiscountCode && !isYotpoDiscount && !isClubPricingDiscount)) {
    return null;
  }

  const clubEnabled =
    line.merchandise.product[`${activeClub}Enabled`]?.value === "true" ||
    isPartnerClub(activeClub);
  const clubPrice = clubEnabled
    ? line.merchandise[activeClub] ?? line.merchandise.product[activeClub]
    : null;
  const parsedPrice = clubPrice?.value ? currencyToFloat(clubPrice.value) : null;

  if (parsedPrice === null) {
    return null;
  }

  return Decimal.min(new Decimal(parsedPrice), line.merchandise.price.amount);
}

export function isYotpoDiscountCode(code: string): boolean {
  if (code === "reindeer" || code === "ollie400") {
    return false;
  }
  return /^[A-Za-z0-9]{8}$/.test(code);
}
